import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useEffect, useState } from 'react'
import { checkActionCode, applyActionCode } from 'firebase/auth'
import { auth } from '@/auth/firebase'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { useCurrentUser } from '@/hooks/useCurrentUser'

// Define the route for handling Firebase email actions
export const Route = createFileRoute('/(onboarding)/(auth)/action')({
    validateSearch: (search) => ({
        mode: typeof search.mode === 'string' ? search.mode : undefined,
        oobCode:
            typeof search.oobCode === 'string' ? search.oobCode : undefined,
        continueUrl:
            typeof search.continueUrl === 'string'
                ? search.continueUrl
                : undefined,
        lang: typeof search.lang === 'string' ? search.lang : undefined,
    }),
    component: ActionRoute,
})

function ActionRoute() {
    const navigate = useNavigate()
    const { mode, oobCode, continueUrl } = useSearch({ from: Route.id })
    const [status, setStatus] = useState<'loading' | 'processing' | 'error'>(
        'loading'
    )
    const [error, setError] = useState<string | null>(null)
    const { user } = useCurrentUser()

    useEffect(() => {
        if (!mode || !oobCode) {
            setError('Invalid action link - missing required parameters.')
            setStatus('error')
            return
        }

        // Sanitize oobCode
        let sanitizedOobCode = oobCode
        try {
            sanitizedOobCode = oobCode.split('&')[0].split('#')[0]
            sanitizedOobCode = decodeURIComponent(sanitizedOobCode)
        } catch {
            // If decoding fails, use original
        }

        const handleAction = async () => {
            try {
                setStatus('processing')

                // Pre-check the action code to get info
                const actionInfo = await checkActionCode(auth, sanitizedOobCode)

                switch (mode) {
                    case 'verifyEmail':
                        // Navigate to verify-email route with the action code
                        navigate({
                            to: '/verify-email',
                            search: {
                                oobCode: sanitizedOobCode,
                                continueUrl,
                            },
                        })
                        break

                    case 'resetPassword':
                        // Navigate to reset-password route with the action code
                        navigate({
                            to: '/reset-password',
                            search: {
                                oobCode: sanitizedOobCode,
                            },
                        })
                        break

                    case 'recoverEmail':
                        // Handle email recovery directly here since we don't have a separate route
                        try {
                            await applyActionCode(auth, sanitizedOobCode)

                            // Show success and redirect
                            const email = actionInfo.data.email
                            alert(
                                `Your email address has been restored to: ${email}`
                            )

                            // Navigate to continue URL or home
                            navigate({ to: continueUrl || '/' })
                        } catch (err: any) {
                            setError(
                                `Failed to recover email: ${err.message || 'Unknown error'}`
                            )
                            setStatus('error')
                        }
                        break

                    default:
                        setError(`Unsupported action mode: ${mode}`)
                        setStatus('error')
                }
            } catch (err: any) {
                const errorCode = err.code || 'unknown'
                let errorMessage = 'Action failed due to an unexpected error.'

                switch (errorCode) {
                    case 'auth/expired-action-code':
                    case 'auth/invalid-action-code':
                        errorMessage =
                            'This action link is invalid or has expired.'
                        break
                    case 'auth/user-disabled':
                        errorMessage = 'This account has been disabled.'
                        break
                    case 'auth/user-not-found':
                        errorMessage =
                            'No account found with this email address.'
                        break
                    default:
                        errorMessage = err.message || errorMessage
                }

                setError(errorMessage)
                setStatus('error')
            }
        }

        handleAction()
    }, [mode, oobCode, continueUrl, navigate])

    const handleGoToLogin = () => {
        navigate({ to: '/login' })
    }

    return (
        <FadeInUp
            delay={0.2}
            className="relative z-10 flex h-full flex-col items-center justify-center px-4"
        >
            {status === 'loading' && (
                <div className="text-center">
                    <p>Processing your request...</p>
                </div>
            )}

            {status === 'processing' && (
                <div className="text-center">
                    <p>Redirecting...</p>
                </div>
            )}

            {status === 'error' && (
                <div className="text-center">
                    <div className="mb-4">
                        <img
                            src={assetPath('icons/status/error.svg')}
                            alt="error"
                            className="mx-auto mb-4"
                            width={56}
                            height={56}
                        />
                    </div>
                    <h1 className="mb-2 text-lg font-semibold">
                        Action Failed
                    </h1>
                    <p className="mb-4">{error}</p>
                    <div className="flex flex-col gap-3">
                        {user ? (
                            <button
                                onClick={() => navigate({ to: '/' })}
                                className="rounded-xl bg-black px-6 py-3 text-white"
                            >
                                Go to Home
                            </button>
                        ) : (
                            <button
                                onClick={handleGoToLogin}
                                className="rounded-xl bg-black px-6 py-3 text-white"
                            >
                                Go to Login
                            </button>
                        )}
                    </div>
                </div>
            )}
        </FadeInUp>
    )
}
